import React, { useEffect, useMemo, useState } from "react"
import { ENG_DIAG_QUESTION_COPY as QCOPY } from "../../../constants/diagnostics/eng-diag.constants"
import type {
  EngDiagQuestion,
  EngDiagStudentAnswers,
} from "../types/eng-diag.types"
import styles from "./eng-diag-question-view.module.css"
import theme from "./eng-diag-theme.module.css"
import { EngDiagRichContent } from "./EngDiagRichContent"

/**
 * A worked-example question type and the plain type it plays as.
 *
 * The author writes the question twice. `options` is the one the child answers and
 * `options.example` is the same shape again, solved, carrying its own `content`. So these seven
 * need no new interaction: show the solved one, then dispatch on the plain twin.
 *
 * `example_problem_pair` is the odd one out - its solved half is plain content in
 * `exampleContent` rather than a nested question - and its own half is answered by typing into
 * blanks, which IS the `blank` type.
 *
 * Mirrors WORKED_TYPE_BASE in LCMS 1.5 (packages/common/src/eng-question-types) and
 * modules/eng-diag/workedTypes.js in tutor-backend, which marks them the same way. Neither repo
 * imports from the other, so the table is repeated; it is the authoring contract and changes
 * only when a type is added there.
 */
export const WORKED_TYPE_BASE: Record<string, string> = {
  mcq_worked: "mcq",
  blank_worked: "blank",
  mc_cloze_worked: "mc_cloze",
  open_ended_worked: "open_ended",
  drag_drop_worked: "spelling_drop",
  reorder_worked: "sentence_rebuild",
  punctuation_insert_worked: "punctuation_insert",
  example_problem_pair: "blank",
}

/** One line of the author's answer to the worked half. */
type WorkedAnswerLine = { label?: string; value: string }

/** One authored choice of the worked half, as something to read rather than to answer. */
type WorkedChoice = { id: string; content: string; isAnswer: boolean }

/** The choices of the worked question, one gap of a worked cloze, or a worked word bank. */
type WorkedChoiceGroup = { label?: string; choices: WorkedChoice[]; inline?: boolean }

/**
 * An authored choice as plain text.
 *
 * Choice content is rich text, and the worked answer is shown as a short line rather than as a
 * block, so the tags come out rather than being rendered. Harmless for everything in the bank:
 * a choice is a word or a sentence, and anything structural in one would not belong on this
 * line anyway.
 */
function plainText(html: unknown): string {
  return String(html ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim()
}

/**
 * The example sentence with its punctuation put in, for `punctuation_insert_worked`.
 *
 * Inserted back to front so that each site's offset - an index into the ORIGINAL sentence -
 * still points where the author meant by the time it is used.
 */
function solvedSentence(example: any): string {
  const sites = [...(example?.sites || [])].sort(
    (a: any, b: any) => Number(b.offset) - Number(a.offset),
  )
  return sites.reduce((text: string, site: any) => {
    const at = Math.max(0, Math.min(text.length, Number(site.offset) || 0))
    return text.slice(0, at) + (site.marks || []).join("") + text.slice(at)
  }, String(example?.sentence ?? ""))
}

/**
 * Every choice the author wrote for the solved half, with the right one flagged.
 *
 * The worked half used to show the answer alone. On a spelling question that throws away most
 * of the lesson: "rider" beside a picture of one says very little, while "rider" against
 * "ridder" and "rideer" IS the rule being taught - the distractors are the thing the child is
 * learning to tell apart (curriculum team, 31 Oct 2026).
 *
 * Only the two types answered by picking an option. A word bank or a sentence to put back in
 * order has no wrong options to read against, so those keep their answer lines untouched.
 *
 * A group is dropped unless it has something to contrast - more than one choice, and one of
 * them marked right. An empty return sends the caller back to the answer lines, so a thin or
 * half-authored example degrades to what it showed before rather than to a list with no tick.
 */
function workedChoiceGroups(
  baseType: string,
  example: any,
  copy: { blank: string; bank: string },
): WorkedChoiceGroup[] {
  const read = (list: any): WorkedChoice[] =>
    (list || [])
      .map((choice: any) => ({
        id: String(choice?.id ?? ""),
        content: String(choice?.content ?? ""),
        isAnswer: Boolean(choice?.isAnswer),
      }))
      // An empty one would render as a blank row and read as a fault in the page.
      .filter((choice: WorkedChoice) => choice.content.trim() !== "")

  const usable = (group: WorkedChoiceGroup) =>
    group.choices.length > 1 && group.choices.some(choice => choice.isAnswer)

  switch (baseType) {
    case "mcq":
      return [{ choices: read(example?.choices) }].filter(usable)

    case "mc_cloze": {
      const blanks = example?.blanks || []
      return blanks
        .map((blank: any, index: number) => ({
          // Numbered only when there are several, like the answer lines it replaces.
          label: blanks.length > 1 ? `${copy.blank} ${index + 1}` : undefined,
          choices: read(blank?.choices),
        }))
        .filter(usable)
    }

    // The bank IS the choice list here. 99% of the uploaded drop examples hold more items than
    // they have blanks, and every spare one was being dropped on the floor - the same loss the
    // mcq case above fixes, on the second-biggest type in the bank.
    case "spelling_drop":
    case "punctuation_drop": {
      const used = new Set(
        (example?.blanks || []).map((blank: any) => String(blank?.correctItemId ?? "")),
      )
      // Bank text is plain, not authored HTML - it goes through the same rich renderer as every
      // other choice, so it has to be escaped on the way in rather than parsed as markup.
      const choices: WorkedChoice[] = (example?.bank || [])
        .map((item: any) => ({
          id: String(item?.id ?? ""),
          content: escapeHtml(String(item?.text ?? "")),
          isAnswer: used.has(String(item?.id ?? "")),
        }))
        .filter((choice: WorkedChoice) => choice.content.trim() !== "")
      // Laid out across the line: a bank runs to 14 items, and one word per row would push the
      // child's half of the question off the bottom of a tablet.
      return [{ label: copy.bank, choices, inline: true }].filter(usable)
    }

    default:
      return []
  }
}

/**
 * Whether the listed choices already say everything the answer lines would.
 *
 * They do for a question answered by picking one option: the ticked choice IS the answer.
 *
 * They do not for a drop question with more than one blank. The bank says which words were
 * used; only the lines say which blank each one went into, and three quarters of the drop
 * examples have two or more. There the two are shown together.
 */
function choicesReplaceLines(baseType: string, example: any): boolean {
  if (baseType === "spelling_drop" || baseType === "punctuation_drop") {
    return (example?.blanks || []).length <= 1
  }
  return true
}

/** Bank text is authored as plain text and rendered as HTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

/**
 * The author's own answer to the worked half, as lines to read.
 *
 * Shown as text, NEVER as the live controls with the answer filled in. A disabled copy of the
 * thing below it invites a child to tap at it and makes the two halves of the question look
 * alike, which is the one thing they must not do.
 *
 * An empty line means the author left that part unanswered - a manually-marked writing question
 * is authored with no answer at all - and the caller drops it, leaving the example as just
 * something to read.
 */
function workedAnswerLines(
  baseType: string,
  example: any,
  copy: { blank: string; gap: string; answer: string },
): WorkedAnswerLine[] {
  const firstAnswer = (entries: any) => String((entries || [])[0]?.value ?? "")
  // Only numbered when there are several: one answer needs no label, and the question above it
  // already shows where it goes.
  const numbered = (label: string, index: number, all: unknown[]) =>
    all.length > 1 ? `${label} ${index + 1}` : undefined

  switch (baseType) {
    case "mcq":
      return (example?.choices || [])
        .filter((choice: any) => choice.isAnswer)
        .map((choice: any) => ({ value: plainText(choice.content) }))

    case "blank":
      return (example?.blanks || []).map((blank: any, index: number, all: unknown[]) => ({
        label: numbered(copy.blank, index, all),
        value: firstAnswer(blank.answer),
      }))

    case "mc_cloze":
      return (example?.blanks || []).map((blank: any, index: number, all: unknown[]) => ({
        label: numbered(copy.gap, index, all),
        value: plainText(
          (blank.choices || []).find((choice: any) => choice.isAnswer)?.content,
        ),
      }))

    case "open_ended":
      return (example?.fields || []).map((field: any, index: number, all: unknown[]) => ({
        label: field.label || numbered(copy.answer, index, all),
        value: firstAnswer(field.answer),
      }))

    case "spelling_drop": {
      // The answer is a bank item id, so it has to be looked up to be read.
      const bank = new Map<string, string>(
        (example?.bank || []).map((item: any) => [String(item.id), String(item.text ?? "")]),
      )
      return (example?.blanks || []).map((blank: any, index: number, all: unknown[]) => ({
        label: blank.label || numbered(copy.blank, index, all),
        value: bank.get(String(blank.correctItemId)) || "",
      }))
    }

    case "sentence_rebuild": {
      // Distractors are in the pool too, and the authored order can legitimately use one.
      const words = new Map<string, string>(
        [...(example?.words || []), ...(example?.distractors || [])].map((word: any) => [
          String(word.id),
          String(word.text ?? ""),
        ]),
      )
      return [
        {
          value: (example?.correctOrder || [])
            .map((id: any) => words.get(String(id)) || "")
            .join(" ")
            .trim(),
        },
      ]
    }

    case "punctuation_insert":
      return [{ value: solvedSentence(example) }]

    default:
      return []
  }
}

type Props = {
  question: EngDiagQuestion
  value?: EngDiagStudentAnswers
  onChange?: (answers: EngDiagStudentAnswers) => void
  /** When true, interactions still work for tutor try-out preview */
  readOnly?: boolean
}

function setAnswer(
  current: EngDiagStudentAnswers,
  key: string,
  next: string | null,
): EngDiagStudentAnswers {
  const copy = { ...current }
  if (next == null || next === "") delete copy[key]
  else copy[key] = next
  return copy
}

/** A, B, C… for a choice list. Falls back to the number past Z, which no question reaches. */
function optionLetter(index: number): string {
  return index < 26 ? String.fromCharCode(65 + index) : String(index + 1)
}

/**
 * The punctuation marks a `punctuation_insert` question offers, for the chip row.
 *
 * The union of the authored bank and every mark the sites will accept, in that order, because
 * the two disagree in the live bank and each carries something the other does not:
 *
 *   - the BANK may hold deliberate distractors. "Drag and drop the FULL STOP" offers
 *     . , ? ! and accepts only the full stop, so taking the sites alone would hand the child
 *     the answer.
 *   - the SITES are the only authoritative statement of what actually fits. Three live
 *     questions were UNANSWERABLE on the bank alone: a brackets question offered one chip
 *     reading "(,)" while its sites wanted "(" and ")" separately, a dashes question offered
 *     "--" while its sites wanted "-", and a hyphens question had a site wanting a comma that
 *     the bank never showed. The child could not produce the mark the question required.
 *
 * Taking both means an authored mark is always reachable and an authored distractor is always
 * kept. The three questions above still want their banks cleaned up - this stops them being
 * impossible meanwhile.
 */
/**
 * How big a free-text answer box should be, taken from the longest answer the AUTHOR wrote.
 *
 * Every box used to be a five-row textarea, so a dictation question asking for the word "sign"
 * gave a child the same acre of paper as a piece of writing - which reads as a request for a
 * paragraph, and is why the curriculum lead asked for this. Sizing off the strand was the
 * first idea and is wrong: `open_ended` is used in Spelling, Grammar, Reading and Vocabulary,
 * and the answers inside one strand run from a single word to a full sentence.
 *
 * An answer nobody authored means a manually-marked question - the writing ones - and that is
 * exactly where the big box belongs.
 */
const ANSWER_BOX = {
  /** Fits on one line at the player's type size. A word, a phrase, a short sentence. */
  WORD_MAX_CHARS: 60,
  /** A sentence or two. Still not a page. */
  SENTENCE_MAX_CHARS: 160,
}

type AnswerBoxSize = "word" | "sentence" | "writing"

/**
 * How big this field's box should be.
 *
 * ⚠️ THIS USED TO READ `field.answer`, WHICH THE STUDENT APP NEVER RECEIVES. stripOptions in
 * tutor-backend removes the answer key before a question is served - correctly - so every field
 * measured zero, every box fell to the largest size, and a question whose model answer is six
 * words handed a child a box the shape of an essay. It worked only in the tutor preview, which
 * is not stripped. That is why the PM saw it "still" oversized after the first attempt.
 *
 * The server now sends `answerSize`, which says how much room to leave and nothing about what to
 * write. The measurement below stays for the tutor preview, where the real answers are present,
 * and its thresholds mirror ANSWER_SIZE in tutor-backend's studentDelivery.js - change the two
 * together.
 */
function answerBoxSize(field: any): AnswerBoxSize {
  const hinted = field?.answerSize
  if (hinted === "word" || hinted === "sentence" || hinted === "writing") return hinted

  const values = (field?.answer || []).map((entry: any) => String(entry?.value ?? ""))
  // An authored newline is the one unambiguous sign that an answer really does span lines.
  if (values.some((value: string) => value.includes("\n"))) return "writing"
  const longest = values.reduce((max: number, value: string) => Math.max(max, value.length), 0)
  // Nothing to go on is not evidence of an essay, so it takes the middle size.
  if (longest === 0) return "sentence"
  if (longest <= ANSWER_BOX.WORD_MAX_CHARS) return "word"
  if (longest <= ANSWER_BOX.SENTENCE_MAX_CHARS) return "sentence"
  return "writing"
}

/**
 * How long a question's own text has to be before it is set beside the answer rather than above
 * it. Roughly a full line at the question's type size.
 */
const SPLIT_MIN_CONTENT_CHARS = 90

/** The tallest an answer box grows to on its own before it starts scrolling. */
const AUTO_GROW_MAX_PX = 320

/**
 * Keeps a textarea the height of what is in it.
 *
 * Which is what makes a smaller starting box safe: the size above is a guess about how much a
 * child will write, and a guess that is too small would otherwise cramp them. This way the box
 * opens at the expected size and grows if they need more.
 */
function autoGrow(element: HTMLTextAreaElement | null) {
  if (!element) return
  element.style.height = "auto"
  element.style.height = `${Math.min(element.scrollHeight, AUTO_GROW_MAX_PX)}px`
}

function punctuationBank(options: any): string[] {
  const authored: string[] = options?.bank || []
  const fromSites: string[] = (options?.sites || []).flatMap(
    (site: { marks?: string[] }) => site?.marks || [],
  )
  const marks = [...new Set([...authored, ...fromSites])]
  // A question with neither is broken, but a child should still see something to tap.
  return marks.length ? marks : [",", ".", "?", "!"]
}

export function EngDiagQuestionView({
  question,
  value,
  onChange,
  readOnly = false,
}: Props) {
  void readOnly
  const [local, setLocal] = useState<EngDiagStudentAnswers>(value || {})
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null)
  const [selectedMark, setSelectedMark] = useState<string | null>(null)

  // Keyed on the SESSION question, not on `value`. The parent hands down a fresh answers object
  // on every keystroke or tap, so depending on it re-ran this after each interaction and wiped
  // the selection: for punctuation_insert - whose own hint reads "Tap a mark, then tap an
  // insertion point" - the student had to re-pick the mark for every site in the sentence.
  //
  // The type whose renderer and answer keys apply. A worked-example type is answered exactly
  // as its plain twin, so everything below switches on this rather than on question.type - and
  // an unknown type passes straight through, which is what keeps the default branch honest.
  const baseType = WORKED_TYPE_BASE[question.type] ?? question.type

  // sessionQuestionKey rather than question.id because the same question can legitimately
  // appear twice in one session (once in the base test, once down a prerequisite chain), and
  // moving between those two must still clear the selection.
  const questionKey = question.sessionQuestionKey ?? question.id

  useEffect(() => {
    setLocal(value || {})
    setSelectedBankId(null)
    setSelectedMark(null)
    // `value` is deliberately not a dependency; see above. When the parent controls the
    // answers, `local` is unused anyway - `answers` below prefers `value`.
  }, [questionKey])

  const answers = value ?? local

  const emit = (next: EngDiagStudentAnswers) => {
    if (!value) setLocal(next)
    onChange?.(next)
  }

  const disabled = false

  // What the student has put in each gap in the sentence: the word picked for a cloze, or the
  // text typed into a blank. Rendered inside the gap so they can read back what they built.
  const gapFills = useMemo(() => {
    const fills: Record<string, string> = {}
    if (baseType === "mc_cloze") {
      for (const blank of question.options?.blanks || []) {
        const chosen = (blank.choices || []).find((c: any) => c.id === answers[blank.id])
        if (chosen) fills[blank.id] = String(chosen.content ?? "")
      }
    } else if (baseType === "blank") {
      // Which covers example_problem_pair, whose own half is a sentence with a gap in it: all
      // four live ones are authored as `<span data-type="answer-placeholder">`, the same
      // placeholder a `blank` uses, so the gap fills itself in as the child types.
      for (const blank of question.options?.blanks || []) {
        if (answers[blank.id]) fills[blank.id] = String(answers[blank.id])
      }
    }
    return fills
  }, [baseType, question.options, answers])

  const placeBankItem = (targetId: string) => {
    if (disabled || !selectedBankId) return
    emit(setAnswer(answers, targetId, selectedBankId))
    setSelectedBankId(null)
  }

  const onDragStart = (itemId: string) => (event: React.DragEvent) => {
    event.dataTransfer.setData("text/plain", itemId)
    setSelectedBankId(itemId)
  }

  const onDropTarget = (targetId: string) => (event: React.DragEvent) => {
    event.preventDefault()
    const itemId = event.dataTransfer.getData("text/plain") || selectedBankId
    if (!itemId || disabled) return
    emit(setAnswer(answers, targetId, itemId))
    setSelectedBankId(null)
  }

  const toggleBankItem = (itemId: string) =>
    setSelectedBankId(prev => (prev === itemId ? null : itemId))

  const renderBank = (bank: Array<{ id: string; text: string }>) => (
    <div className={styles.engDiagSection}>
      <span className={styles.engDiagSectionLabel}>{QCOPY.WORD_BANK}</span>
      <div className={styles.engDiagPanel}>
        {bank.map(item => (
          <button
            key={item.id}
            type="button"
            // Placing a chip does NOT use it up: one bank word can be the answer to several
            // targets (the /ir/ in "bird", "girl" and "shirt" is one question, not three), so
            // unlike the sort and the rebuild, nothing here is ever removed or ghosted.
            draggable={!disabled}
            onDragStart={onDragStart(item.id)}
            className={`${styles.engDiagBankItem} ${
              selectedBankId === item.id ? styles.engDiagBankItemActive : ""
            }`}
            aria-pressed={selectedBankId === item.id}
            disabled={disabled}
            onClick={() => toggleBankItem(item.id)}
          >
            {item.text}
          </button>
        ))}
      </div>
    </div>
  )

  /**
   * A question with nothing for the student to answer: they read aloud, or just look at
   * something, and a tutor marks it from the lesson report afterwards.
   *
   * Before this, all four of those types fell through to the default branch and a child was
   * shown "Unsupported type: read_words".
   */
  const renderNoAnswerNote = (icon: string, title: string, body: string) => (
    <div className={styles.engDiagNoAnswerNote}>
      <span className={styles.engDiagNoAnswerIcon} aria-hidden="true">
        <i className={icon} />
      </span>
      <div>
        <div className={styles.engDiagNoAnswerTitle}>{title}</div>
        <div className={styles.engDiagNoAnswerBody}>{body}</div>
      </div>
    </div>
  )

  /** The words of a read-aloud list, laid out the number-per-row the author chose. */
  const renderWordList = () => {
    const words = question.options?.words || []
    const perRow = Math.max(1, Number(question.options?.wordsPerRow) || 3)
    return (
      <div
        className={styles.engDiagWordList}
        style={{ gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))` }}
      >
        {words.map((word: any) => (
          <div key={word.id} className={styles.engDiagWordListItem}>
            {word.text}
          </div>
        ))}
      </div>
    )
  }

  /** One multiple-choice list. Shared by `mcq` and by an mcq comprehension sub-question. */
  const renderChoices = (
    choices: Array<{ id: string; content: string }>,
    answerKey: string,
    label: string,
  ) => (
    <div className={styles.engDiagChoices} role="radiogroup" aria-label={label}>
      {choices.map((choice, index) => {
        const on = answers[answerKey] === choice.id
        const choose = () => emit(setAnswer(answers, answerKey, choice.id))
        return (
          // A div rather than a button, because an option's content can itself contain a
          // control: a listening question puts the clip IN the option, and the play button
          // for it cannot live inside a <button>. role/tabIndex/keydown keep it behaving and
          // reading as the radio it was.
          <div
            key={choice.id}
            role="radio"
            tabIndex={0}
            aria-checked={on}
            className={`${styles.engDiagChoice} ${on ? styles.engDiagChoiceActive : ""}`}
            onClick={choose}
            onKeyDown={event => {
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault()
                choose()
              }
            }}
          >
            <span
              className={`${styles.engDiagChoiceBadge} ${
                on ? styles.engDiagChoiceBadgeActive : ""
              }`}
              aria-hidden="true"
            >
              {optionLetter(index)}
            </span>
            {/* Through the rich renderer, not raw HTML: LCMS stores a clip in an option as an
                empty placeholder span, so the three Reception letter-sound questions showed
                four blank options - audible in LCMS, silent in the app. */}
            <EngDiagRichContent
              className={`${styles.engDiagChoiceText} ${
                on ? styles.engDiagChoiceTextActive : ""
              }`}
              html={choice.content}
            />
            {/* Only on the chosen option, so it reads as a confirmation rather than as a
                checkbox the student has to go and find. */}
            {on ? (
              <span className={styles.engDiagChoiceTick} aria-hidden="true">
                <i className="fas fa-check" />
              </span>
            ) : null}
          </div>
        )
      })}
    </div>
  )

  /**
   * The solved half of a Skill Builder question, when it has one: something to read before
   * answering the half below it.
   *
   * Null for every diagnostics question, and for a worked type whose example is missing. That
   * last case renders as the plain question rather than as an error - the child can still answer
   * it, which is better than a dead end, and the gap shows up in the bank review instead.
   */
  const worked = ((): {
    content: string
    lines: WorkedAnswerLine[]
    groups: WorkedChoiceGroup[]
  } | null => {
    if (question.type === "example_problem_pair") {
      const content = String(question.options?.exampleContent ?? "")
      return content ? { content, lines: [], groups: [] } : null
    }
    const example = question.options?.example
    if (!example) return null
    const groups = workedChoiceGroups(baseType, example, {
      blank: QCOPY.BLANK_LABEL,
      bank: QCOPY.WORD_BANK,
    })
    return {
      content: String(example.content ?? ""),
      // The listed choices already carry the answer, ticked. Keeping the line as well would
      // state it twice, immediately below itself.
      lines:
        groups.length > 0 && choicesReplaceLines(baseType, example)
          ? []
          : workedAnswerLines(baseType, example, {
              blank: QCOPY.BLANK_LABEL,
              gap: QCOPY.GAP_LABEL,
              answer: QCOPY.YOUR_ANSWER,
            }).filter(line => line.value.trim() !== ""),
      groups,
    }
  })()

  const renderWorked = (solved: {
    content: string
    lines: WorkedAnswerLine[]
    groups: WorkedChoiceGroup[]
  }) => (
    <section className={styles.engDiagWorked} aria-label={QCOPY.WORKED_LABEL}>
      <span className={styles.engDiagWorkedLabel}>{QCOPY.WORKED_LABEL}</span>
      {/* Through the rich renderer like every other authored field: the example is written in
          the same editor and can carry a picture, a table or a clip. */}
      <EngDiagRichContent
        className={styles.engDiagWorkedContent}
        html={solved.content}
      />
      {/* Every choice, the right one ticked. Deliberately NOT the control from below with its
          answer filled in: no badge letter, no border, no ledge, nothing that reads as
          pressable. The two halves must not look alike - a child who thinks the example is
          answerable answers it instead of the question. */}
      {solved.groups.length > 0 ? (
        <div className={styles.engDiagWorkedChoices}>
          {solved.groups.map((group, groupIndex) => (
            <div key={groupIndex} className={styles.engDiagWorkedChoiceGroup}>
              {group.label ? (
                <span className={styles.engDiagWorkedAnswerLabel}>{group.label}</span>
              ) : null}
              {/* The live classes, not a copy of them: the example is supposed to look like the
                  question it is an example OF, and sharing the selectors is what stops the two
                  drifting apart the next time the option card is restyled. What the worked copy
                  adds is only what must differ - static, one per row in the narrower column,
                  and green for correct instead of the navy that means "you picked this". */}
              <div
                className={
                  group.inline
                    ? styles.engDiagBankRow
                    : `${styles.engDiagChoices} ${styles.engDiagWorkedChoiceGrid}`
                }
              >
                {group.choices.map((choice, choiceIndex) => (
                  <div
                    key={choice.id || choiceIndex}
                    className={[
                      group.inline ? styles.engDiagBankItem : styles.engDiagChoice,
                      styles.engDiagWorkedCard,
                      choice.isAnswer ? styles.engDiagWorkedCardRight : "",
                    ].join(" ")}
                  >
                    {/* A word bank item is just the word - the live bank has no badge either. */}
                    {group.inline ? null : (
                      <span
                        className={`${styles.engDiagChoiceBadge} ${
                          choice.isAnswer ? styles.engDiagWorkedBadgeRight : ""
                        }`}
                        aria-hidden="true"
                      >
                        {optionLetter(choiceIndex)}
                      </span>
                    )}
                    {/* Through the rich renderer like the live list: a choice can be a picture
                        or carry a clip, and raw HTML would show neither. */}
                    <EngDiagRichContent
                      className={group.inline ? undefined : styles.engDiagChoiceText}
                      html={choice.content}
                    />
                    {choice.isAnswer ? (
                      <span
                        className={`${styles.engDiagChoiceTick} ${styles.engDiagWorkedTickRight}`}
                        aria-hidden="true"
                      >
                        <i className="fas fa-check" />
                      </span>
                    ) : null}
                    {choice.isAnswer ? (
                      <span className={styles.engDiagSrOnly}>{QCOPY.WORKED_CORRECT}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {solved.lines.length > 0 ? (
        <dl className={styles.engDiagWorkedAnswers}>
          {solved.lines.map((line, index) => (
            <div key={index} className={styles.engDiagWorkedAnswerRow}>
              <dt className={styles.engDiagWorkedAnswerLabel}>
                {line.label || QCOPY.WORKED_ANSWER}
              </dt>
              <dd className={styles.engDiagWorkedAnswerValue}>{line.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  )

  /**
   * The question's own text.
   *
   * Split out from the body so it can be the left-hand column of a two-pane question as well as
   * the line above a stacked one.
   */
  const renderContent = () => (
    // Content, not plain HTML: a listening question carries its clip as a placeholder span
    // inside it, which renders as nothing without this.
    <EngDiagRichContent
      className={styles.engDiagContent}
      html={question.content || ""}
      gapFills={gapFills}
    />
  )

  /**
   * Whether this question reads better as two columns.
   *
   * Long stem, and answered in a control of its own - a sentence to correct, a passage to
   * rewrite. Those are the ones where a child re-reads the text while typing, and stacked, the
   * text is above the fold by the time they do.
   *
   * NOT keyed on the strand chip ("WRITING", "READING"), though that is what the PM pointed at:
   * the chip is the CLUSTER name and a layout that changes with it would move whenever the
   * curriculum team rename something. Shape is the durable signal.
   *
   * Multiple choice is excluded on purpose - its options are the continuation of the question,
   * not a separate place to answer - as are the drag types, whose targets ARE the sentence.
   */
  const splitContent =
    (baseType === "blank" || baseType === "open_ended") &&
    plainText(question.content).length >= SPLIT_MIN_CONTENT_CHARS

  /**
   * The half the child answers: the question itself and its controls.
   *
   * Hoisted because a worked question wraps it in a column beside the solved half and every
   * other question does not. Written once so the two layouts cannot drift.
   */
  const renderQuestionHalf = () => (
    <>
      {renderContent()}
      {renderBody()}
    </>
  )

  const renderBody = () => {
    switch (baseType) {
      case "mcq":
        return renderChoices(
          question.options?.choices || [],
          question.id,
          "Answer options",
        )

      case "blank": {
        const blanks = question.options?.blanks || []
        // One blank needs no numbering - the sentence above already shows where it goes. Several
        // do, because the inputs sit in a column away from the gaps they fill.
        if (blanks.length === 1) {
          const blank = blanks[0]
          const typed = answers[blank.id] || ""
          return (
            <div className={styles.engDiagField}>
              <label className={styles.engDiagFieldLabel} htmlFor={`ed-${blank.id}`}>
                {QCOPY.TYPE_YOUR_ANSWER}
              </label>
              <input
                id={`ed-${blank.id}`}
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={QCOPY.TYPE_HERE}
                className={`${styles.engDiagInput} ${
                  typed.trim() ? styles.engDiagInputFilled : ""
                }`}
                value={typed}
                onChange={e => emit(setAnswer(answers, blank.id, e.target.value))}
              />
            </div>
          )
        }
        return (
          <div className={styles.engDiagRows}>
            {blanks.map((blank: any, index: number) => {
              const typed = answers[blank.id] || ""
              return (
                <div key={blank.id} className={styles.engDiagRow}>
                  <span className={styles.engDiagRowIndex} aria-hidden="true">
                    {index + 1}
                  </span>
                  <label className={styles.engDiagFieldLabel} htmlFor={`ed-${blank.id}`}>
                    {QCOPY.BLANK_LABEL} {index + 1}
                  </label>
                  <input
                    id={`ed-${blank.id}`}
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={QCOPY.TYPE_YOUR_ANSWER}
                    className={`${styles.engDiagInput} ${
                      typed.trim() ? styles.engDiagInputFilled : ""
                    }`}
                    style={{ flex: "1 1 240px", width: "auto" }}
                    value={typed}
                    onChange={e => emit(setAnswer(answers, blank.id, e.target.value))}
                  />
                </div>
              )
            })}
          </div>
        )
      }
      case "open_ended": {
        // Authors set the order; without this the fields came out in whatever order the stored
        // JSON happened to hold, which for a two-part question is the wrong two parts.
        const fields = [...(question.options?.fields || [])].sort(
          (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0),
        )
        const renderControl = (field: any, inRow: boolean) => {
          const typed = answers[field.id] || ""
          const size = answerBoxSize(field)
          const filled = typed.trim() ? styles.engDiagInputFilled : ""
          const onChange = (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => emit(setAnswer(answers, field.id, event.target.value))
          if (size === "word") {
            return (
              <input
                id={`ed-${field.id}`}
                type="text"
                autoComplete="off"
                spellCheck={false}
                placeholder={QCOPY.TYPE_HERE}
                className={`${styles.engDiagInput} ${filled}`}
                // Same as a `blank` row: the box shares the line with its number and label
                // rather than taking the full width and pushing them onto their own.
                style={inRow ? { flex: "1 1 240px", width: "auto" } : undefined}
                value={typed}
                onChange={onChange}
              />
            )
          }
          return (
            <textarea
              id={`ed-${field.id}`}
              spellCheck={false}
              placeholder={QCOPY.WRITE_HERE}
              className={`${styles.engDiagTextarea} ${
                size === "sentence" ? styles.engDiagTextareaShort : ""
              } ${filled}`}
              style={inRow ? { flex: "1 1 280px", width: "auto" } : undefined}
              value={typed}
              // Sized on mount as well as on change, so a resumed answer opens at its own
              // height rather than at the empty one.
              ref={autoGrow}
              onChange={event => {
                autoGrow(event.target)
                onChange(event)
              }}
            />
          )
        }
        // One field needs no number - the question above it says what to write. Several do,
        // and they get the numbered rows a multi-blank question already uses, because the
        // boxes sit in a column away from the thing they answer. This is what makes the five
        // dictated words readable as 1..5.
        if (fields.length === 1) {
          const field = fields[0]
          return (
            <div className={styles.engDiagField}>
              <label className={styles.engDiagFieldLabel} htmlFor={`ed-${field.id}`}>
                {field.label || QCOPY.YOUR_ANSWER}
              </label>
              {renderControl(field, false)}
            </div>
          )
        }
        return (
          <div className={styles.engDiagRows}>
            {fields.map((field: any, index: number) => (
              <div key={field.id} className={styles.engDiagRow}>
                <span className={styles.engDiagRowIndex} aria-hidden="true">
                  {index + 1}
                </span>
                <label className={styles.engDiagFieldLabel} htmlFor={`ed-${field.id}`}>
                  {field.label || `${QCOPY.YOUR_ANSWER} ${index + 1}`}
                </label>
                {renderControl(field, true)}
              </div>
            ))}
          </div>
        )
      }
      case "mc_cloze": {
        const blanks = question.options?.blanks || []
        return (
          <div className={styles.engDiagRows}>
            {blanks.map((blank: any, index: number) => (
              <div key={blank.id} className={styles.engDiagRow}>
                <span className={styles.engDiagRowIndex} aria-hidden="true">
                  {index + 1}
                </span>
                <span className={styles.engDiagRowPrompt}>
                  {QCOPY.GAP_LABEL} {index + 1}
                </span>
                <div
                  className={styles.engDiagBankRow}
                  role="radiogroup"
                  aria-label={`${QCOPY.GAP_LABEL} ${index + 1}`}
                >
                  {(blank.choices || []).map((choice: any) => {
                    const on = answers[blank.id] === choice.id
                    return (
                      <button
                        key={choice.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        className={`${styles.engDiagChip} ${
                          on ? styles.engDiagChipActive : ""
                        }`}
                        onClick={() => emit(setAnswer(answers, blank.id, choice.id))}
                      >
                        {choice.content}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      }
      case "spelling_drop":
      case "punctuation_drop":
      // A column match is the same interaction with the left column standing in for the blanks,
      // so it maps its pairs onto this player rather than repeating it.
      case "column_match": {
        const blanks =
          question.type === "column_match"
            ? (question.options?.pairs || []).map((pair: any) => ({
                id: pair.id,
                // A left side that is a PICTURE is authored with an empty leftText, and
                // falling back to the id printed a raw database key beside the image for
                // the child to read ("QUJEBFZm8Dz0"). The row is numbered already, so a
                // picture-only side needs no label; the id is kept only for the case where
                // there is neither text nor image, where it is the one clue to what broke.
                label: pair.leftText || (pair.leftImageUrl ? "" : pair.id),
                imageUrl: pair.leftImageUrl,
              }))
            : question.options?.blanks || []
        const bank = question.options?.bank || []
        const armed = selectedBankId !== null
        return (
          <>
            <p className={styles.engDiagHint}>{QCOPY.DROP_HINT}</p>
            {renderBank(bank)}
            <div className={styles.engDiagTargets}>
              {blanks.map((blank: any, index: number) => {
                const placedId = answers[blank.id]
                const placed = bank.find((b: any) => b.id === placedId)
                return (
                  <div
                    key={blank.id}
                    className={`${styles.engDiagDropTarget} ${
                      placed ? styles.engDiagDropTargetFilled : ""
                    }`}
                    onDragOver={e => e.preventDefault()}
                    onDrop={onDropTarget(blank.id)}
                    onClick={() => placeBankItem(blank.id)}
                  >
                    <span className={styles.engDiagRowIndex} aria-hidden="true">
                      {index + 1}
                    </span>
                    {/* A spelling drop prompts with a picture, and a column match may do the
                        same on the left. Without this the target showed only its label, which
                        for a picture-prompted blank is nothing at all. */}
                    {blank.imageUrl && (
                      <img
                        src={blank.imageUrl}
                        alt={blank.label || ""}
                        className={styles.engDiagDropTargetImage}
                      />
                    )}
                    {blank.label && (
                      <span className={styles.engDiagDropTargetLabel}>{blank.label}</span>
                    )}
                    <span
                      className={`${styles.engDiagSlot} ${
                        placed
                          ? styles.engDiagSlotFilled
                          : armed
                            ? styles.engDiagSlotArmed
                            : ""
                      }`}
                    >
                      <span>
                        {placed
                          ? placed.text
                          : armed
                            ? QCOPY.DROP_ARMED
                            : QCOPY.DROP_EMPTY}
                      </span>
                      {/* Dropping a different word on a filled space already replaces what is
                          there, but nothing could EMPTY one: a child who placed a word by
                          mistake was stuck with it, and a space they meant to leave blank was
                          marked wrong. Its own button rather than a click on the slot, so that
                          replacing still takes one move. stopPropagation so clearing does not
                          immediately re-place the word that is still selected. */}
                      {placed ? (
                        <button
                          type="button"
                          className={styles.engDiagSlotClear}
                          aria-label={`Clear ${placed.text}`}
                          title="Clear"
                          onClick={event => {
                            event.stopPropagation()
                            emit(setAnswer(answers, blank.id, null))
                          }}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      ) : null}
                    </span>
                  </div>
                )
              })}
            </div>
          </>
        )
      }
      case "image_sort": {
        const categories = question.options?.categories || []
        const items = question.options?.items || []
        /*
         * A sorted card LEAVES the pool rather than dimming inside it. Dimmed-but-still-there
         * read as spent, so a child who wanted to move a card had no reason to try the pool
         * again - and the gesture they did try, dragging it back out of the group, did nothing,
         * because a card in a group was a label and not a control. The pool is now what is left
         * to sort, and a card in a group is what moves it or takes it back.
         */
        const unsorted = items.filter((item: any) => !answers[item.id])
        const armed = selectedBankId !== null
        const place = (itemId: string, categoryId: string) => {
          emit(setAnswer(answers, itemId, categoryId))
          setSelectedBankId(null)
        }
        // The type is called image_sort and its items carry an imageUrl, which was never
        // rendered: a picture card came out as its database id.
        const cardFace = (item: any) =>
          item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt={item.text || ""}
              className={styles.engDiagDropTargetImage}
            />
          ) : (
            <span>{item.text || item.id}</span>
          )
        return (
          <>
            <p className={styles.engDiagHint}>{QCOPY.SORT_HINT}</p>
            <div className={styles.engDiagSection}>
              <span className={styles.engDiagSectionLabel}>{QCOPY.SORT_POOL_LABEL}</span>
              <div className={styles.engDiagPanel}>
                {unsorted.map((item: any) => (
                  <button
                    key={item.id}
                    type="button"
                    draggable={!disabled}
                    onDragStart={onDragStart(item.id)}
                    className={`${styles.engDiagSortItem} ${
                      selectedBankId === item.id ? styles.engDiagBankItemActive : ""
                    }`}
                    aria-pressed={selectedBankId === item.id}
                    onClick={() => toggleBankItem(item.id)}
                  >
                    {cardFace(item)}
                  </button>
                ))}
                {!unsorted.length ? (
                  <span className={styles.engDiagHint}>{QCOPY.SORT_POOL_EMPTY}</span>
                ) : null}
              </div>
            </div>
            <div className={styles.engDiagBins}>
              {categories.map((cat: any) => {
                const inCat = items.filter((item: any) => answers[item.id] === cat.id)
                return (
                  <div key={cat.id} className={styles.engDiagBin}>
                    {/* The head is a drop control too, and it lights up while a card is
                        selected, so the child is shown where the card can go before they
                        tap. */}
                    <button
                      type="button"
                      className={`${styles.engDiagBinHead} ${
                        armed ? styles.engDiagBinHeadArmed : ""
                      }`}
                      onClick={() => {
                        if (!selectedBankId) return
                        place(selectedBankId, cat.id)
                      }}
                    >
                      <span>{cat.name}</span>
                      <span className={styles.engDiagBinCount}>{inCat.length}</span>
                    </button>
                    <div
                      className={`${styles.engDiagBinBody} ${
                        armed ? styles.engDiagBinBodyArmed : ""
                      }`}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => {
                        e.preventDefault()
                        const itemId =
                          e.dataTransfer.getData("text/plain") || selectedBankId
                        if (!itemId) return
                        place(itemId, cat.id)
                      }}
                      onClick={() => {
                        if (!selectedBankId) return
                        place(selectedBankId, cat.id)
                      }}
                    >
                      {inCat.map((item: any) => (
                        <button
                          key={item.id}
                          type="button"
                          /* Draggable so a card can go straight from one group to another, and
                             clickable so it can go back to the pool: the two ways a child
                             changes their mind. */
                          draggable={!disabled}
                          onDragStart={onDragStart(item.id)}
                          className={styles.engDiagSortedChip}
                          aria-label={`Take ${item.text || item.id} out of ${cat.name}`}
                          onClick={event => {
                            event.stopPropagation()
                            emit(setAnswer(answers, item.id, null))
                          }}
                        >
                          {cardFace(item)}
                          <span aria-hidden="true" className={styles.engDiagChipRemove}>
                            <i className="fas fa-times" />
                          </span>
                        </button>
                      ))}
                      {!inCat.length ? (
                        <span className={styles.engDiagBinEmpty}>{QCOPY.SORT_BIN_EMPTY}</span>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )
      }
      case "punctuation_insert": {
        const sentence = question.options?.sentence || ""
        const sites: Array<{ id: string; offset: number; marks?: string[] }> =
          question.options?.sites || []
        const bank = punctuationBank(question.options)
        const siteByOffset = new Map<
          number,
          { id: string; offset: number; marks?: string[] }
        >(sites.map(s => [Number(s.offset), s]))
        const chars = sentence.split("")
        const renderSite = (
          site?: { id: string; offset: number; marks?: string[] }
        ) =>
          site ? (
            <button
              key={`site-${site.id}`}
              type="button"
              className={`${styles.engDiagSiteBtn} ${
                answers[site.id] ? styles.engDiagSiteBtnFilled : ""
              }`}
              aria-label={
                answers[site.id]
                  ? `Change the mark here, currently ${answers[site.id]}`
                  : "Put a mark here"
              }
              onClick={() => {
                if (!selectedMark) return
                emit(setAnswer(answers, site.id, selectedMark))
              }}
            >
              {answers[site.id] || "‸"}
            </button>
          ) : null
        return (
          <>
            <div className={styles.engDiagSection}>
              <span className={styles.engDiagSectionLabel}>{QCOPY.MARKS_LABEL}</span>
              <div className={styles.engDiagMarkBank}>
                {bank.map(mark => (
                  <button
                    key={mark}
                    type="button"
                    className={`${styles.engDiagChip} ${styles.engDiagMark} ${
                      selectedMark === mark ? styles.engDiagChipActive : ""
                    }`}
                    aria-pressed={selectedMark === mark}
                    onClick={() => setSelectedMark(prev => (prev === mark ? null : mark))}
                  >
                    {mark}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.engDiagPassage}>
              {chars.map((ch: string, index: number) => (
                <React.Fragment key={`${index}-${ch}`}>
                  {renderSite(siteByOffset.get(index))}
                  <span>{ch}</span>
                </React.Fragment>
              ))}
              {/* A site at offset === sentence.length is the end-of-sentence position, used by a
                  trailing full stop or question mark. There is no character to render it before,
                  so it is emitted after the last one - without this the button is never drawn at
                  all and the question cannot be answered. */}
              {renderSite(siteByOffset.get(chars.length))}
            </div>
            <p className={styles.engDiagHint}>{QCOPY.INSERT_HINT}</p>
          </>
        )
      }
      case "text_select": {
        const mode = question.options?.mode || "word"
        const passage = question.options?.passage || ""
        const selected = new Set(
          (answers.selected || "").split("|").filter(Boolean),
        )
        const tokens =
          mode === "letter"
            ? passage.split("").map((t: string, i: number) => ({
                key: `L:${i}:${t}`,
                text: t,
              }))
            : passage.split(/(\s+)/).map((t: string, i: number) => ({
                key: `W:${i}:${t}`,
                text: t,
              }))
        return (
          <>
            {/* A count, not a "choose n" instruction: how many to pick is part of what is being
                assessed, so it is never given away - but a child who has tapped three words
                should be able to see that they have. */}
            <span
              className={`${styles.engDiagCounter} ${
                selected.size ? styles.engDiagCounterOn : ""
              }`}
            >
              {selected.size} {QCOPY.SELECT_COUNT_SUFFIX}
            </span>
            <div className={`${styles.engDiagPassage} ${styles.engDiagWordRow}`}>
              {tokens.map((token: { key: string; text: string }) => {
                if (!token.text.trim()) {
                  return <span key={token.key}>{token.text}</span>
                }
                const active = selected.has(token.key)
                return (
                  <button
                    key={token.key}
                    type="button"
                    aria-pressed={active}
                    className={`${styles.engDiagSelectToken} ${
                      mode === "letter" ? styles.engDiagMark : ""
                    } ${active ? styles.engDiagSelectTokenActive : ""}`}
                    onClick={() => {
                      const next = new Set(selected)
                      if (next.has(token.key)) next.delete(token.key)
                      else next.add(token.key)
                      emit(
                        setAnswer(answers, "selected", Array.from(next).join("|")),
                      )
                    }}
                  >
                    {token.text}
                  </button>
                )
              })}
            </div>
            <p className={styles.engDiagHint}>{QCOPY.SELECT_HINT}</p>
          </>
        )
      }
      case "sentence_rebuild": {
        const words = [
          ...(question.options?.words || []),
          ...(question.options?.distractors || []),
        ]
        const order = (answers.order || "").split("|").filter(Boolean)
        const placedIds = new Set(order)
        const setOrder = (next: string[]) =>
          emit(setAnswer(answers, "order", next.join("|")))
        return (
          <>
            <p className={styles.engDiagHint}>{QCOPY.REBUILD_HINT}</p>
            <div className={styles.engDiagSection}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className={styles.engDiagSectionLabel}>{QCOPY.REBUILD_LABEL}</span>
                {/* Taking seven words back out one at a time is how a child gives up on a
                    question they could do. */}
                {order.length ? (
                  <button
                    type="button"
                    className={styles.engDiagBankItem}
                    style={{ fontSize: 18 }}
                    onClick={() => setOrder([])}
                  >
                    {QCOPY.REBUILD_RESET}
                  </button>
                ) : null}
              </div>
              {/* The bank words were already draggable, but nothing accepted a drop - this row
                  had no onDragOver - so every drag ended with the word back where it started
                  and the question looked broken. Tapping always worked, which is why it went
                  unnoticed.

                  No numbered empty slots, unlike the mockup: how many words the correct
                  sentence has is stripped out of what the student receives, and the bank can
                  hold distractors, so one slot per bank word would say that every word is
                  used. */}
              <div
                className={styles.engDiagRebuildZone}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault()
                  const wordId =
                    e.dataTransfer.getData("text/plain") || selectedBankId
                  if (!wordId || placedIds.has(wordId)) return
                  setOrder([...order, wordId])
                  setSelectedBankId(null)
                }}
              >
                {order.map(id => {
                  const word = words.find((w: any) => w.id === id)
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`${styles.engDiagWord} ${styles.engDiagWordActive}`}
                      aria-label={`Take ${word?.text || id} out of the sentence`}
                      onClick={() => setOrder(order.filter(x => x !== id))}
                    >
                      {word?.text || id}
                    </button>
                  )
                })}
                {!order.length ? (
                  <span className={styles.engDiagHint}>{QCOPY.REBUILD_EMPTY}</span>
                ) : null}
              </div>
            </div>
            <div className={styles.engDiagSection}>
              <span className={styles.engDiagSectionLabel}>{QCOPY.WORD_BANK}</span>
              <div className={styles.engDiagPanel}>
                {words.map((word: any) =>
                  placedIds.has(word.id) ? (
                    // A placeholder the same size as the word, so taking one out of the bank
                    // does not reflow it and move the word the child was about to tap next.
                    <span
                      key={word.id}
                      className={styles.engDiagChipGhost}
                      aria-hidden="true"
                    >
                      {word.text}
                    </span>
                  ) : (
                    <button
                      key={word.id}
                      type="button"
                      className={styles.engDiagWord}
                      draggable
                      onDragStart={onDragStart(word.id)}
                      onClick={() => setOrder([...order, word.id])}
                    >
                      {word.text}
                    </button>
                  ),
                )}
              </div>
            </div>
          </>
        )
      }
      case "reading_comp": {
        const subQuestions = question.options?.subQuestions || []
        return (
          // Passage on the left, questions on the right, wherever there is room for both. A
          // comprehension is answered by looking BACK at the text, and stacked the passage
          // scrolled off the top as soon as the child reached question two - so they were
          // answering from memory, which is not what is being assessed. Below the breakpoint it
          // stacks as before, passage first.
          <div className={styles.engDiagSplit}>
            {/* Same renderer as the question content: a passage is authored in the same
                editor, so a clip or a gap placed in one would otherwise render as nothing. */}
            <EngDiagRichContent
              className={`${styles.engDiagPassage} ${styles.engDiagProse} ${styles.engDiagSplitSide}`}
              html={question.options?.passage || ""}
            />
            {/* Two kinds of sub-question, each answered under its own id. Comprehension is
                authored as multiple choice now, but questions written before that switch are
                still being sat, and they are marked on typed text. A sub-question written
                before the kind was recorded at all has no `type` - that means typed, never
                mcq, which is the same reading the contract and the marker take. */}
            <div className={`${styles.engDiagRows} ${styles.engDiagSplitMain}`}>
              {subQuestions.map((sq: any, index: number) => (
                <div key={sq.id} className={styles.engDiagField}>
                  <div className="d-flex align-items-start gap-3">
                    <span className={styles.engDiagRowIndex} aria-hidden="true">
                      {index + 1}
                    </span>
                    {/* A label only where there is an input to label. A multiple-choice
                        sub-question is a radio group, named by aria-label below, and a `for`
                        pointing at nothing is worse than no label at all. */}
                    {sq.type === "mcq" ? (
                      <span className={styles.engDiagRowPrompt}>{sq.prompt}</span>
                    ) : (
                      <label className={styles.engDiagRowPrompt} htmlFor={`ed-${sq.id}`}>
                        {sq.prompt}
                      </label>
                    )}
                  </div>
                  {sq.type === "mcq" ? (
                    renderChoices(sq.choices || [], sq.id, sq.prompt)
                  ) : (
                    <input
                      id={`ed-${sq.id}`}
                      type="text"
                      autoComplete="off"
                      placeholder={QCOPY.TYPE_YOUR_ANSWER}
                      className={`${styles.engDiagInput} ${
                        (answers[sq.id] || "").trim() ? styles.engDiagInputFilled : ""
                      }`}
                      value={answers[sq.id] || ""}
                      onChange={e => emit(setAnswer(answers, sq.id, e.target.value))}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      }
      /*
       * The four types with nothing for the student to answer. Their responses are recorded
       * empty and marked by a human from the lesson report, which is why NOTHING_TO_ANSWER in
       * EngDiagAttemptPlayer lets the student submit one without touching anything.
       *
       * All four used to fall through to the default branch, so a child was shown
       * "Unsupported type: read_words" and the word list they were meant to read aloud was not
       * drawn at all.
       */
      case "read_words":
        return (
          <>
            {renderNoAnswerNote(
              "fas fa-comment-dots",
              QCOPY.READ_WORDS_TITLE,
              QCOPY.READ_WORDS_BODY,
            )}
            {renderWordList()}
          </>
        )
      case "phoneme_segmentation":
        return (
          <>
            {renderNoAnswerNote(
              "fas fa-comment-dots",
              QCOPY.PHONEMES_TITLE,
              QCOPY.PHONEMES_BODY,
            )}
            {renderWordList()}
          </>
        )
      case "flashcard":
        return (
          <>
            {renderNoAnswerNote(
              "fas fa-bolt",
              QCOPY.FLASHCARD_TITLE,
              QCOPY.FLASHCARD_BODY,
            )}
            {/* The authored showDelay / displayDuration timings are NOT played here: a timed
                presentation is a feature of its own, and this type is marked by the tutor
                either way. Showing the cards is what it needs to stop being a blank screen. */}
            <div className={styles.engDiagWordList} style={{ gridTemplateColumns: "1fr" }}>
              {(question.options?.cards || []).map((card: any) => (
                <div key={card.id} className={styles.engDiagWordListItem}>
                  {card.text}
                </div>
              ))}
            </div>
          </>
        )
      case "display_content":
        // Nothing but the content above, which is the whole point of the type.
        return renderNoAnswerNote(
          "fas fa-eye",
          QCOPY.DISPLAY_TITLE,
          QCOPY.DISPLAY_BODY,
        )
      default:
        return (
          <p className={styles.engDiagHint}>
            Unsupported type: {question.type}
          </p>
        )
    }
  }

  return (
    <div className={`${theme.engDiagTheme} ${styles.engDiagRoot}`}>
      {/* Authored in a rich-text editor, so it is HTML: 552 of the 560 questions have tags in
          it, and as plain text a child was shown "<p>Choose the sentence…</p>" and "&quot;"
          verbatim. Routed through the same component as the content so a clip placed in the
          instruction plays rather than vanishing. */}
      {question.instruction ? (
        <EngDiagRichContent
          className={styles.engDiagInstruction}
          html={question.instruction}
        />
      ) : null}
      {/* A worked question is laid out as two halves - the solved one and the child's - side by
          side where the screen is wide enough for it, because the point is to read one WHILE
          answering the other. Without a worked example the markup is exactly what it was: no
          wrapper, so nothing about a diagnostics question changed. */}
      {worked ? (
        <div className={styles.engDiagSplit}>
          {renderWorked(worked)}
          <section className={styles.engDiagSplitMain} aria-label={QCOPY.YOUR_TURN}>
            <span className={styles.engDiagWorkedLabel}>{QCOPY.YOUR_TURN}</span>
            {renderQuestionHalf()}
          </section>
        </div>
      ) : splitContent ? (
        <div className={styles.engDiagSplit}>
          <div className={styles.engDiagSplitSide}>{renderContent()}</div>
          <div className={styles.engDiagSplitMain}>{renderBody()}</div>
        </div>
      ) : (
        renderQuestionHalf()
      )}
    </div>
  )
}
